import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import './TipoServicoManager.css'

/**
 * Componente para gerenciar tipos de serviço
 * Permite criar, editar, ativar/desativar e visualizar tipos de serviço
 */
const TipoServicoManager = ({ onBack, userRole }) => {
  const [tiposServico, setTiposServico] = useState([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingTipo, setEditingTipo] = useState(null)
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    valor_servico: '',
    ativo: true
  })
  const [message, setMessage] = useState({ show: false, text: '', type: '' })

  useEffect(() => {
    loadTiposServico()
  }, [])

  const loadTiposServico = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('tipo_servico')
        .select('*')
        .order('nome')

      if (error) throw error
      setTiposServico(data || [])
    } catch (error) {
      console.error('Erro ao carregar tipos de serviço:', error)
      showMessage('Erro ao carregar tipos de serviço: ' + error.message, 'error')
    } finally {
      setLoading(false)
    }
  }

  const showMessage = (text, type = 'success') => {
    setMessage({ show: true, text, type })
    setTimeout(() => {
      setMessage({ show: false, text: '', type: '' })
    }, 4000)
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const formatCurrencyInput = (value) => {
    // Remove tudo que não é dígito
    const numbers = value.replace(/\D/g, '')
    
    if (!numbers) return ''
    
    // Converte para número e divide por 100
    const amount = parseFloat(numbers) / 100
    
    // Formata com separador de milhar e decimal brasileiro
    return amount.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  const parseCurrencyBR = (value) => {
    // Converte formato brasileiro (14.524,25) para número (14524.25)
    if (!value) return 0
    const cleanValue = value.replace(/\./g, '').replace(',', '.')
    return parseFloat(cleanValue) || 0
  }

  const handleCurrencyChange = (e) => {
    const formatted = formatCurrencyInput(e.target.value)
    setFormData(prev => ({ ...prev, valor_servico: formatted }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.nome.trim()) {
      showMessage('Nome é obrigatório', 'error')
      return
    }

    try {
      setLoading(true)

      const dataToSave = {
        nome: formData.nome.trim(),
        descricao: formData.descricao.trim() || null,
        valor_servico: formData.valor_servico ? parseCurrencyBR(formData.valor_servico) : 0,
        ativo: formData.ativo
      }

      if (editingTipo) {
        // Atualizar
        const { error } = await supabase
          .from('tipo_servico')
          .update(dataToSave)
          .eq('id', editingTipo.id)

        if (error) throw error
        showMessage('Tipo de serviço atualizado com sucesso!', 'success')
      } else {
        // Criar
        const { error } = await supabase
          .from('tipo_servico')
          .insert([dataToSave])

        if (error) throw error
        showMessage('Tipo de serviço cadastrado com sucesso!', 'success')
      }

      resetForm()
      loadTiposServico()
    } catch (error) {
      console.error('Erro ao salvar tipo de serviço:', error)
      showMessage('Erro ao salvar: ' + error.message, 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (tipo) => {
    setEditingTipo(tipo)
    setFormData({
      nome: tipo.nome || '',
      descricao: tipo.descricao || '',
      valor_servico: tipo.valor_servico 
        ? parseFloat(tipo.valor_servico).toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })
        : '',
      ativo: tipo.ativo !== false
    })
    setShowForm(true)
  }

  const handleToggleStatus = async (tipo) => {
    try {
      setLoading(true)
      const { error } = await supabase
        .from('tipo_servico')
        .update({ ativo: !tipo.ativo })
        .eq('id', tipo.id)

      if (error) throw error
      showMessage(`Tipo de serviço ${!tipo.ativo ? 'ativado' : 'desativado'} com sucesso!`, 'success')
      loadTiposServico()
    } catch (error) {
      console.error('Erro ao alterar status:', error)
      showMessage('Erro ao alterar status: ' + error.message, 'error')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      nome: '',
      descricao: '',
      valor_servico: '',
      ativo: true
    })
    setEditingTipo(null)
    setShowForm(false)
  }

  const formatCurrencyDisplay = (value) => {
    if (!value) return 'R$ 0,00'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  // Verificar se o usuário tem permissão (apenas administrador e gerente)
  if (userRole !== 'administrador' && userRole !== 'gerente') {
    return (
      <div className="tipo-servico-manager">
        <div className="manager-header">
          <h2>⚙️ Tipos de Serviço</h2>
          <button onClick={onBack} className="btn-back">
            Voltar
          </button>
        </div>
        <div className="access-denied">
          <div className="access-denied-icon">🚫</div>
          <h3>Acesso Negado</h3>
          <p>Apenas administradores e gerentes podem gerenciar tipos de serviço.</p>
        </div>
      </div>
    )
  }

  if (showForm) {
    return (
      <div className="tipo-servico-manager">
        <div className="manager-header">
          <h2>{editingTipo ? '✏️ Editar Tipo de Serviço' : '➕ Novo Tipo de Serviço'}</h2>
          <button onClick={resetForm} className="btn-back">
            Voltar
          </button>
        </div>

        <form onSubmit={handleSubmit} className="tipo-servico-form">
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="nome">Nome do Tipo de Serviço *</label>
              <input
                type="text"
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
                required
                placeholder="Ex: Instalação de Sistema Fotovoltaico"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="descricao">Descrição</label>
              <textarea
                id="descricao"
                name="descricao"
                value={formData.descricao}
                onChange={handleInputChange}
                rows="4"
                placeholder="Descreva os detalhes deste tipo de serviço..."
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="valor_servico">Valor do Serviço (R$)</label>
              <div className="input-group">
                <span className="input-group-text">R$</span>
                <input
                  type="text"
                  id="valor_servico"
                  name="valor_servico"
                  value={formData.valor_servico}
                  onChange={handleCurrencyChange}
                  placeholder="0,00"
                  disabled={loading}
                />
              </div>
              <small className="form-text">Valor padrão cobrado por este tipo de serviço</small>
            </div>

            <div className="form-group-checkbox">
              <input
                type="checkbox"
                id="ativo"
                name="ativo"
                checked={formData.ativo}
                onChange={handleInputChange}
                disabled={loading}
              />
              <label htmlFor="ativo">Tipo de serviço ativo</label>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={resetForm} className="btn btn-secondary" disabled={loading}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? '⏳ Salvando...' : editingTipo ? '✓ Atualizar' : '✓ Cadastrar'}
            </button>
          </div>
        </form>
      </div>
    )
  }

  return (
    <div className="tipo-servico-manager">
      <div className="manager-header">
        <h2>⚙️ Gerenciar Tipos de Serviço</h2>
        <div className="header-actions">
          <button onClick={() => setShowForm(true)} className="btn btn-primary">
            ➕ Novo Tipo de Serviço
          </button>
          <button onClick={onBack} className="btn-back">
            ← Voltar
          </button>
        </div>
      </div>

      {message.show && (
        <div className={`message-alert ${message.type}`}>
          {message.type === 'success' ? '✅' : '❌'} {message.text}
        </div>
      )}

      {loading && <div className="loading">⏳ Carregando...</div>}

      <div className="tipos-list">
        <div className="list-header">
          <h3>📋 Tipos de Serviço Cadastrados ({tiposServico.length})</h3>
        </div>

        {tiposServico.length === 0 ? (
          <div className="empty-state">
            <p>📋 Nenhum tipo de serviço cadastrado.</p>
            <button onClick={() => setShowForm(true)} className="btn btn-primary">
              Cadastrar Primeiro Tipo
            </button>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Descrição</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {tiposServico.map((tipo) => (
                  <tr key={tipo.id} className={!tipo.ativo ? 'row-inactive' : ''}>
                    <td className="tipo-nome">{tipo.nome}</td>
                    <td className="tipo-descricao">
                      {tipo.descricao || <span className="no-data">Sem descrição</span>}
                    </td>
                    <td className="tipo-valor">
                      {formatCurrencyDisplay(tipo.valor_servico)}
                    </td>
                    <td className="tipo-status">
                      <span className={`status-badge ${tipo.ativo ? 'status-ativo' : 'status-inativo'}`}>
                        {tipo.ativo ? '✅ Ativo' : '❌ Inativo'}
                      </span>
                    </td>
                    <td className="tipo-acoes">
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(tipo)}
                          className="btn-action edit"
                          title="Editar tipo de serviço"
                          disabled={loading}
                        >
                          ✏️ Editar
                        </button>
                        <button
                          onClick={() => handleToggleStatus(tipo)}
                          className={`btn-action ${tipo.ativo ? 'deactivate' : 'activate'}`}
                          title={tipo.ativo ? 'Desativar' : 'Ativar'}
                          disabled={loading}
                        >
                          {tipo.ativo ? '🔴 Desativar' : '🟢 Ativar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default TipoServicoManager

